import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bestDealProductList } from "../../Redux/Actions/ProductActions";
import Loading from "../LoadingError/Loading";
import Message from "../LoadingError/Error";
import { DEFAULT_CURRENCY, IMAGE_CACHE_URL } from "../../Redux/Constants/GlobalConstant";
import QuantityInput from "../QuantityInput";

const AUTO_SPEED   = 0.5;  // px/frame when no hover
const HOVER_MAX    = 5;    // max px/frame at the edges
const DEAD_ZONE    = 0.15; // 15% center dead-zone (no scroll)

const BestDealSection = () => {
  const dispatch = useDispatch();
  const { loading, error, products = [] } = useSelector((s) => s.bestDealProductList);
  const cart = useSelector((state) => state.cart);
  const { cartItems = [] } = cart;

  const scrollRef   = useRef(null);
  const animRef     = useRef(null);
  const posRef      = useRef(0);
  const hoveringRef = useRef(false);   // true while mouse is inside
  const hoverSpeed  = useRef(0);       // negative = left, positive = right

  // drag state
  const dragging    = useRef(false);
  const startX      = useRef(0);
  const startScroll = useRef(0);
  const didDrag     = useRef(false);

  const [showLeft,  setShowLeft]  = useState(false);
  const [showRight, setShowRight] = useState(true);

  useEffect(() => { dispatch(bestDealProductList()); }, [dispatch]);

  // ── Unified animation tick ────────────────────────────────────────────────
  const tick = useCallback(() => {
    const el = scrollRef.current;
    if (el && !dragging.current) {
      let speed;
      if (hoveringRef.current) {
        speed = hoverSpeed.current; // cursor-controlled
      } else {
        speed = AUTO_SPEED; // default auto-right
      }

      const half = el.scrollWidth / 2;
      posRef.current += speed;
      // Clamp within the doubled list for seamless loop
      if (posRef.current >= half) posRef.current -= half;
      if (posRef.current < 0)     posRef.current += half;
      el.scrollLeft = posRef.current;

      setShowLeft(posRef.current > 10);
      setShowRight(true);
    }
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!loading && !error && products.length > 0) {
      animRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [loading, error, products, tick]);

  // ── Cursor-position → scroll speed ──────────────────────────────────────
  const onMouseMove = (e) => {
    const el = scrollRef.current;
    if (!el || dragging.current) return;
    const { left, width } = el.getBoundingClientRect();
    const rel = (e.clientX - left) / width; // 0 (left edge) → 1 (right edge)
    const centered = rel - 0.5;             // -0.5 → 0.5

    if (Math.abs(centered) < DEAD_ZONE) {
      hoverSpeed.current = 0;            // dead-zone — pause
    } else {
      // Map to -HOVER_MAX … HOVER_MAX, scaled by distance from center
      const factor = (Math.abs(centered) - DEAD_ZONE) / (0.5 - DEAD_ZONE);
      hoverSpeed.current = Math.sign(centered) * factor * HOVER_MAX;
    }
  };

  const onMouseEnter = () => { hoveringRef.current = true; };
  const onMouseLeave = () => {
    hoveringRef.current = false;
    hoverSpeed.current  = 0;
    if (!dragging.current && scrollRef.current)
      scrollRef.current.style.cursor = "default";
  };

  // ── Arrow buttons ────────────────────────────────────────────────────────
  const slide = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    posRef.current = Math.max(0, posRef.current + (dir === "left" ? -220 : 220));
    el.scrollLeft = posRef.current;
  };

  // ── Mouse drag ────────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.clientX;
    startScroll.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = "grabbing";
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !scrollRef.current) return;
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > 4) didDrag.current = true;
      const next = startScroll.current - dx;
      scrollRef.current.scrollLeft = next;
      posRef.current = next;
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (scrollRef.current) scrollRef.current.style.cursor = "default";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup",   up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup",   up);
    };
  }, []);

  // ── Touch drag (mobile) ───────────────────────────────────────────────────
  const onTouchStart = (e) => {
    hoveringRef.current = false; // disable hover-scroll on touch
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.touches[0].clientX;
    startScroll.current = scrollRef.current.scrollLeft;
  };

  const onTouchMove = (e) => {
    if (!dragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > 4) didDrag.current = true;
    const next = startScroll.current - dx;
    scrollRef.current.scrollLeft = next;
    posRef.current = next;
  };

  const onTouchEnd = () => {
    dragging.current = false;
  };

  const guardClick = (e) => { if (didDrag.current) e.preventDefault(); };

  return (
    <div className="container mobile-container">
      <div className="section">
        <div className="row mobile-thumbnail">
          <div className="col-lg-12 col-md-12 article">
            <h5>Best Deal of the Day</h5>
            <div className="bd-scroll-wrapper">

              <button className={`bd-nav-btn bd-nav-btn--left ${showLeft ? "bd-nav-btn--visible" : ""}`} onClick={() => slide("left")} aria-label="Scroll left">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>

              <div
                className="best-deal-shopcontainer row bd-track"
                ref={scrollRef}
                onMouseMove={onMouseMove}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={{ userSelect: "none" }}
              >
                {loading ? <div className="mb-5"><Loading /></div>
                  : error ? <Message key={1} variant="alert-danger">{error}</Message>
                  : <>
                    {[...products, ...products].map((product, i) => (
                      <div className="shop col-lg-2 col-md-3 col-sm-3 bd-card" key={i}>
                        <div className="border-product">
                          <Link to={`/products/${product._id}`} onClick={guardClick}>
                            <div className="shopBack">
                              <img src={IMAGE_CACHE_URL + product.image} alt={product.name} draggable="false" style={{ pointerEvents: "none" }} />
                            </div>
                          </Link>
                          <div className="shoptext">
                            <p><Link to={`/products/${product._id}`} onClick={guardClick}>{product.name} - {product?.unit}</Link></p>
                            <div className="price-button-thumb-sec">
                              <h3>
                                {product.mrp_price - product.price > 0 && <span className="text-overline">{DEFAULT_CURRENCY} {product.mrp_price}</span>}
                                <span>{DEFAULT_CURRENCY} {product.price}</span>
                              </h3>
                              <QuantityInput productId={product._id} renderAddButton={true} qty={cartItems.find(x => x.product === product._id)?.qty ?? 0} max_order={product?.max_order ?? null} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                }
              </div>

              <button className={`bd-nav-btn bd-nav-btn--right ${showRight ? "bd-nav-btn--visible" : ""}`} onClick={() => slide("right")} aria-label="Scroll right">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestDealSection;
